import type { CalendarDayPhrases, ModifiersShape } from 'react-dates';

import type { Moment } from 'moment';

const BLOCKED_MODIFIER = 'blocked';

function getPhrase(phrase: unknown, args: Record<string, string>) {
  if (typeof phrase === 'string') return phrase;

  if (typeof phrase === 'function') {
    return phrase(args);
  }

  return '';
}

function isSelected(modifiers: ModifiersShape) {
  return (
    modifiers.has('selected') ||
    modifiers.has('selected-span') ||
    modifiers.has('selected-start') ||
    modifiers.has('selected-end')
  );
}

function shouldUseDefaultCursor(modifiers: ModifiersShape) {
  return (
    modifiers.has('blocked-minimum-nights') ||
    modifiers.has('blocked-calendar') ||
    modifiers.has('blocked-out-of-range')
  );
}

function isHoveredSpan(modifiers: ModifiersShape) {
  if (isSelected(modifiers)) return false;
  return modifiers.has('hovered-span') || modifiers.has('after-hovered-start') || modifiers.has('before-hovered-end');
}

function getAriaLabel(phrases: CalendarDayPhrases, modifiers: ModifiersShape, day: Moment, ariaLabelFormat: string) {
  const { chooseAvailableDate, dateIsUnavailable, dateIsSelected, dateIsSelectedAsStartDate, dateIsSelectedAsEndDate } =
    phrases;

  const formattedDate = {
    date: day.format(ariaLabelFormat),
  };

  if (modifiers.has('selected-start') && dateIsSelectedAsStartDate) {
    return getPhrase(dateIsSelectedAsStartDate, formattedDate);
  }
  if (modifiers.has('selected-end') && dateIsSelectedAsEndDate) {
    return getPhrase(dateIsSelectedAsEndDate, formattedDate);
  }
  if (isSelected(modifiers) && dateIsSelected) {
    return getPhrase(dateIsSelected, formattedDate);
  }
  if (modifiers.has(BLOCKED_MODIFIER)) {
    return getPhrase(dateIsUnavailable, formattedDate);
  }

  return getPhrase(chooseAvailableDate, formattedDate);
}

export default function getCalendarDaySettings(
  day: Moment,
  ariaLabelFormat: string,
  daySize: number,
  modifiers: ModifiersShape,
  phrases: CalendarDayPhrases
) {
  return {
    ariaLabel: getAriaLabel(phrases, modifiers, day, ariaLabelFormat),
    hoveredSpan: isHoveredSpan(modifiers),
    isOutsideRange: modifiers.has('blocked-out-of-range'),
    selected: isSelected(modifiers),
    useDefaultCursor: shouldUseDefaultCursor(modifiers),

    daySizeStyles: {
      width: daySize,
      height: daySize - 1,
    },
  };
}
